import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard } from "../App";
import { toast } from "react-toastify";
import { Oval } from "react-loader-spinner";
import { RoleGuard } from "../App";
import { StandardURL } from "../App";

const ArchivedDataTransactions = () => {
  const propertyId = localStorage.getItem("selectedPropertyId");
  const [transactions, setTransactions] = useState([]);
  const [year, setYear] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchTransactions = () => {
    setIsLoading(true);
    const token = localStorage.getItem("token");
    axios
      .post(
        `${StandardURL}/iNyumba/alltransactionsviewbyyear/${propertyId}`,
        { year },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        setTransactions(response.data.response);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching archived transactions:", error);
        toast.error(
          error.response?.data?.response ||
            "Error fetching archived transactions"
        );
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchTransactions();
  }, [propertyId, year]);

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-200">
            {/* --- Header Section --- */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
              <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight flex items-center">
                Archived Transactions 🗄️
              </h1>
            </div>

            <p className="text-gray-600 mb-8 max-w-3xl">
              Browse historical transaction data by selecting a specific year.
            </p>

            {/* --- Year Selection --- */}
            <div className="mb-8 p-6 bg-gray-50 rounded-lg shadow-inner border border-gray-100 flex items-center gap-4 max-w-sm">
              <label htmlFor="year-select" className="block text-lg font-semibold text-gray-800 flex-shrink-0">
                <svg className="w-6 h-6 inline-block mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h.01M7 16h.01M17 16h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                Select Year:
              </label>
              <input
                type="number"
                id="year-select"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="flex-grow px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-medium text-gray-700"
                placeholder="e.g., 2023"
                min="1900" // Example minimum year
                max={new Date().getFullYear()} // Example maximum year
              />
            </div>

            {/* --- Loading State --- */}
            {isLoading ? (
              <div className="flex justify-center items-center py-10">
                <Oval
                  height={50}
                  width={50}
                  color="#4F46E5" // A nice indigo color
                  ariaLabel="loading-indicator"
                  secondaryColor="#E0E7FF" // Lighter shade for spinner background
                  strokeWidth={5}
                  strokeWidthSecondary={5}
                />
                <p className="ml-4 text-lg text-gray-700 font-medium">Loading archived transactions for {year}...</p>
              </div>
            ) : (
              /* --- Transactions Table --- */
              <div className="overflow-x-auto relative shadow-lg rounded-lg border border-gray-100">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                    <tr>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider rounded-tl-lg">Date</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Transaction ID</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Channel</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wider">Amount</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wider rounded-tr-lg">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {transactions.length > 0 ? (
                      transactions.map((transaction) => (
                        <tr
                          key={transaction.transaction_id}
                          className="hover:bg-gray-50 transition-colors duration-150 ease-in-out"
                        >
                          <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {new Date(transaction.created_at).toLocaleDateString("en-KE")}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-700">
                            <span className="font-mono bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">
                                {transaction.transaction_id}
                            </span>
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-700">
                            <span className="font-semibold text-blue-700">
                                {transaction.reference}
                            </span>
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-right font-bold text-green-600">
                            Ksh {parseFloat(transaction.amount).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-right font-bold text-indigo-700">
                            Ksh {parseFloat(transaction.running_balance).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="py-8 px-4 text-center text-lg text-gray-500 italic">
                          <div className="flex flex-col items-center justify-center">
                            <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                            No archived transactions found for {year}.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default ArchivedDataTransactions;
